import { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import './Admin.css';

function DeletedUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDeletedUsers = async () => {
    try {
      const data = await api.get('/api/admin/users/deleted');
      setUsers(data.users);
    } catch (err) {
      console.error('Failed to fetch deleted users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeletedUsers(); }, []);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
      u.username.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  const handleReactivate = async () => {
    if (!reactivateTarget) return;
    setActionLoading(true);
    try {
      await api.put(`/api/admin/users/${reactivateTarget.id}/reactivate`);
      setUsers(prev => prev.filter(u => u.id !== reactivateTarget.id));
      setReactivateTarget(null);
    } catch (err) {
      console.error('Reactivate failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Deleted Users</h1>
          <p>Previously deactivated accounts that can be restored</p>
        </div>
      </div>

      <div className="admin-toolbar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="admin-search"
            placeholder="Search by name, username, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="admin-search-clear" onClick={() => setSearchQuery('')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <span className="admin-count">
          {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
        </span>
      </div>

      <div className="admin-table-card">
        {filteredUsers.length === 0 ? (
          <div className="admin-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <line x1="18" y1="8" x2="23" y2="13" />
              <line x1="23" y1="8" x2="18" y2="13" />
            </svg>
            <h3>No deleted users</h3>
            <p>{searchQuery ? 'Try adjusting your search' : 'Deactivated accounts will appear here'}</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td className="admin-name-cell">
                    <div className="admin-avatar" style={{ opacity: 0.5 }}>
                      {user.firstName[0]}{user.lastName[0]}
                    </div>
                    <span style={{ opacity: 0.7 }}>{user.firstName} {user.lastName}</span>
                  </td>
                  <td style={{ opacity: 0.7 }}>{user.username}</td>
                  <td className="admin-email-cell">{user.email}</td>
                  <td>
                    <span className={`admin-role-badge ${user.role}`}>{user.role}</span>
                  </td>
                  <td className="admin-date-cell">
                    {new Date(user.updatedAt).toLocaleDateString()}
                  </td>
                  <td>
                    <button
                      className="admin-action-btn reactivate"
                      onClick={() => setReactivateTarget(user)}
                    >
                      Reactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Reactivate Confirmation */}
      {reactivateTarget && (
        <div className="admin-modal-overlay">
          <div className="admin-confirm-dialog">
            <h3>Reactivate User?</h3>
            <p>
              This will restore <strong>{reactivateTarget.firstName} {reactivateTarget.lastName}</strong> ({reactivateTarget.username})
              and allow them to sign in again with their existing credentials.
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-modal-btn-cancel" onClick={() => setReactivateTarget(null)} disabled={actionLoading}>
                Cancel
              </button>
              <button className="admin-action-btn reactivate" onClick={handleReactivate} disabled={actionLoading} style={{ padding: '8px 18px' }}>
                {actionLoading ? 'Reactivating...' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeletedUsers;
